/**
 * SEMANTIC TOKENS — Purpose-Based Design Tokens
 *
 * These tokens describe PURPOSE, not appearance.
 * They use actual values from primitives/aliases, NOT PrimeNG '{reference}' syntax.
 *
 * Token Hierarchy:
 *   Primitives (raw values) → Aliases (brand mapping) → **Semantic (this file)** → Component Tokens → PrimeNG Presets
 *
 * Origin key:
 *   🎨 Figma  — Exported from Figma (semantics.tokens.json). Matches Figma variable names.
 *   🔧 Custom — Added to bridge gaps between Figma semantics and component-token needs.
 *               Not present in the Figma export.
 */

import * as a from './aliases.js';
import * as p from './primitives.js';

// ════════════════════════════════════════════════════════════════════════════
// TEXT TOKENS — Text colors by intent
// Figma: Semantics / Text  (12 tokens)
// ════════════════════════════════════════════════════════════════════════════

export const text = {
  // ── 🎨 Figma ──
  primary: a.primary[800],       // #c14f79  — Text/primary        → Rentals/800
  secondary: a.secondary[800],   // #08183a  — Text/secondary      → Rentsody/700
  tertiary: a.secondary[700],    // #212f4e  — Text/tertiary       → Rentsody/700
  muted: a.secondary[400],       // #6B7489  — Text/muted          → Rentsody/400
  onColor: a.surface.white,      // #ffffff  — Text/on-color       → White
  disabled: p.neutral.white,     // #ffffff  — Text/disabled       → Neutral/200 → cambiado a White
  success: a.success[800],       // #357e57  — Text/success        → Green/800
  danger: a.danger[800],         // #9b0310  — Text/danger         → Red/800
  warning: a.warning[800],       // #856404  — Text/warning        → Yellow/800
  link: a.primary[800],          // #c14f79  — Text/link           → Rentals/800
  linkHover: a.primary[900],     // #ae476d  — Text/link-hover     → Rentals/900
  linkDisabled: p.neutral[200],  // #b5bac4  — Text/link-disabled  → Neutral/200
} as const;

// ════════════════════════════════════════════════════════════════════════════
// SURFACE TOKENS — Background / surface colors
// Figma: Semantics / Surface  (6 tokens)
// ════════════════════════════════════════════════════════════════════════════

export const surface = {
  // ── 🎨 Figma ──
  ground: a.surface.white,       // #ffffff  — Surface/ground   → White
  canvas: p.neutral[50],         // #e7e8ec  — Surface/canvas   → Neutral/50
  raised: p.neutral[50],         // #e7e8ec  — Surface/raised   → Neutral/50
  modal: a.surface.white,        // #ffffff  — Surface/modal    → White
  disabled: p.neutral[200],      // #b5bac4  — Surface/disabled → Neutral/200
  dark: p.neutral[950],          // #1d1d1d  — Surface/dark     → Neutral/950
} as const;

// ════════════════════════════════════════════════════════════════════════════
// BORDER TOKENS — Border colors by intent
// Figma: Semantics / Border  (10 tokens)
// ════════════════════════════════════════════════════════════════════════════

export const border = {
  // ── 🎨 Figma ──
  default: p.neutral[200],       // #b5bac4  — Border/default   → Neutral/200
  strong: p.neutral[600],        // #394661  — Border/strong    → Rentsody/600
  divider: p.neutral[50],        // #e7e8ec  — Border/divider   → Neutral/50
  focus: a.primary[800],         // #c14f79  — Border/focus     → Rentals/800
  disabled: p.neutral[200],      // #b5bac4  — Border/disabled  → Neutral/200
  primary: a.primary[800],       // #c14f79  — Border/primary   → Rentals/800
  secondary: a.secondary[800],   // #08183a  — Border/secondary → Rentsody/800
  success: a.success[800],       // #357e57  — Border/success   → Green/800
  danger: a.danger[800],         // #9b0310  — Border/danger    → Red/800
  warning: a.warning[800],       // #856404  — Border/warning   → Yellow/800

  // ── 🔧 Custom ──
  /** Input-specific focus border (secondary brand, softer than primary focus) */
  focusInput: a.secondary[400],  // #6b7489  — Not in Figma
} as const;

// ════════════════════════════════════════════════════════════════════════════
// STATE TOKENS — Feedback / status colors (bg + fg + border per state)
// Figma: Semantics / States  (4 groups × 3 = 12 tokens)
// ════════════════════════════════════════════════════════════════════════════

export const states = {
  // ── 🎨 Figma ──
  success: {
    bg: a.success[50],           // #f3f7f4  — States/Success/bg
    fg: a.success[800],          // #357e57  — States/Success/fg
    border: a.success[800],      // #357e57  — States/Success/border
  },
  warning: {
    bg: a.warning[50],           // #fef4cd  — States/Warning/bg
    fg: a.warning[800],          // #856404  — States/Warning/fg
    border: a.warning[800],      // #856404  — States/Warning/border
  },
  danger: {
    bg: a.danger[50],            // #fbe6e8  — States/Danger/bg
    fg: a.danger[800],           // #9b0310  — States/Danger/fg
    border: a.danger[800],       // #9b0310  — States/Danger/border
  },
  info: {
    bg: p.neutral[50],           // #e7e8ec  — States/Info/bg
    fg: p.neutral[800],          // #08183a  — States/Info/fg
    border: p.neutral[800],      // #08183a  — States/Info/border
  },
} as const;

// ════════════════════════════════════════════════════════════════════════════
// FOCUS TOKENS
// Figma: Semantics / Focus  (2 tokens)
// ════════════════════════════════════════════════════════════════════════════

export const focus = {
  // ── 🎨 Figma ──
  ring: a.primary[800],          // #c14f79  — Focus/ring        → Rentals/800
  ringOffset: a.surface.white,   // #ffffff  — Focus/ring-offset → White
} as const;

// ════════════════════════════════════════════════════════════════════════════
// LAYOUT TOKENS — Spacing
// Figma: Semantics / Layout  (Gap: 3, Padding: 3)
// ════════════════════════════════════════════════════════════════════════════

export const layout = {
  gap: {
    // ── 🔧 Custom ──
    xs: p.spacing[4],   // 0.25rem (4px)  — Not in Figma (used by 12+ components)
    // ── 🎨 Figma ──
    sm: p.spacing[8],   // 0.5rem  (8px)  — Layout/Gap/gap-sm  → Spacing/8
    md: p.spacing[16],  // 1rem    (16px) — Layout/Gap/gap-md  → Spacing/16
    lg: p.spacing[24],  // 1.5rem  (24px) — Layout/Gap/gap-lg  → Spacing/24
    // ── 🔧 Custom ──
    xl: p.spacing[32],  // 2rem    (32px) — Not in Figma (used by message, steps, toolbar)
  },
  padding: {
    // ── 🔧 Custom ──
    xs: p.spacing[8],   // 0.5rem  (8px)  — Not in Figma (used by 10+ components)
    // ── 🎨 Figma ──
    sm: p.spacing[24],  // 1.5rem  (24px) — Layout/Padding/padding-sm → Spacing/24
    md: p.spacing[48],  // 3rem    (48px) — Layout/Padding/padding-md → Spacing/48
    lg: p.spacing[56],  // 3.5rem  (56px) — Layout/Padding/padding-lg → Spacing/56
  },
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 RADIUS TOKENS — Semantic border-radius
// Entirely custom — Figma does not export semantic radius tokens
// ════════════════════════════════════════════════════════════════════════════

export const radius = {
  sm: p.borderRadius[4],   // 0.25rem (4px)  — small (tags, pills)
  md: p.borderRadius[8],   // 0.5rem  (8px)  — default (buttons, inputs, cards)
  lg: p.borderRadius[16],  // 1rem    (16px) — large (modals, panels)
  xl: p.borderRadius[24],  // 1.5rem  (24px) — extra large
  full: '9999px',          // Fully rounded (circles, pills)
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 SHADOW TOKENS — Semantic elevation shadows
// Entirely custom — Figma does not export semantic shadow tokens
// ════════════════════════════════════════════════════════════════════════════

export const shadow = {
  sm: p.shadows.sm,     // subtle shadow  (small elevation)
  md: p.shadows.md,     // default shadow (menus, dropdowns)
  lg: p.shadows.lg,     // large shadow   (modals, dialogs)
  card: p.shadows.card, // card shadow    (cards, raised surfaces)
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 TYPOGRAPHY TOKENS — Font family, size, weight, line-height
// Entirely custom — Figma does not export semantic typography tokens
// (Figma defines typography at the primitive level only)
// ════════════════════════════════════════════════════════════════════════════

export const typography = {
  fontFamily: {
    base: p.fontFamily.base,         // 'Poppins', system-ui, -apple-system, sans-serif
  },
  fontSize: {
    xs: p.fontSize.xs,       // 0.75rem  (12px)
    sm: p.fontSize.sm,       // 0.875rem (14px)
    base: p.fontSize.base,   // 1rem     (16px)
    md: p.fontSize.base,     // 1rem     (16px) — alias for preset compatibility
    lg: p.fontSize.lg,       // 1.125rem (18px)
    xl: p.fontSize.xl,       // 1.25rem  (20px)
  },
  fontWeight: {
    normal: p.fontWeight.normal,     // 400
    semibold: p.fontWeight.semibold, // 600
    bold: p.fontWeight.bold,         // 700
  },
  lineHeight: {
    base: p.lineHeight.base,         // 1rem    (16px)
    normal: 'normal',                // CSS normal (~1.5)
    lg: p.lineHeight.lg,             // 1.25rem (20px)
    xl: p.lineHeight.xl,             // 1.5rem  (24px)
    '2xl': p.lineHeight['2xl'],      // 1.75rem (28px)
  },
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 TRANSITION TOKENS — Animation timing
// Entirely custom — Figma does not export transition tokens
// ════════════════════════════════════════════════════════════════════════════

export const transition = {
  normal: '0.3s',    // Standard interactions (tabs, accordions, inputs)
  fast: '150ms',     // Quick feedback (hovers, toggles)
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 ICON SIZE TOKENS — Consistent icon dimensions
// Entirely custom — Figma does not export semantic icon-size tokens
// ════════════════════════════════════════════════════════════════════════════

export const iconSizes = {
  base: '1rem',      // 16px  — small inline icons
  md: '1.25rem',     // 20px  — medium icons (toast)
  lg: '1.5rem',      // 24px  — standard icons (buttons, nav, controls)
  xl: '1.5rem',      // 24px  — large icons (dropdowns, datepicker cells)
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 CONTROL SIZE TOKENS — Standard interactive-element heights
// Entirely custom — Figma does not export semantic control-size tokens
// ════════════════════════════════════════════════════════════════════════════

export const controlSize = {
  sm: '2rem',        // 32px  — compact controls
  base: '2.5rem',     // 40px  — default controls (buttons, inputs)
  md: '2.75rem',     // 44px  — standard controls (buttons, inputs, paginator)
  lg: '3rem',        // 48px  — large controls
} as const;

// ════════════════════════════════════════════════════════════════════════════
// 🔧 INTERACTIVE TOKENS — Interaction-state colors for components
// Entirely custom — Figma does not export interactive-state semantics.
// These bridge the gap between base semantic colors (text, border, surface)
// and the hover / pressed / subtle shades that component tokens need.
// Without them, component tokens would reference primitive/alias shades
// directly, breaking the semantic abstraction.
// ════════════════════════════════════════════════════════════════════════════

export const interactive = {
  /** Primary brand interaction states */
  primary: {
    base: a.primary[800],            // Base primary action color
    hover: a.primary[900],           // Hover state for primary actions
    pressed: a.primary[950],         // Pressed/active state for primary actions
    subtle: a.primary[50],           // Subtle primary surface (light tint)
    subtlePressed: a.primary[100],   // Subtle primary pressed state
  },
  /** Secondary brand interaction states */
  secondary: {
    base: a.secondary[800],          // Base secondary action color
    hover: a.secondary[700],         // Hover state for secondary actions
    pressed: a.secondary[950],       // Pressed/active state for secondary actions
    subtle: a.secondary[50],         // Subtle secondary surface (light tint)
    subtleHover: a.secondary[100],   // Subtle secondary hover state
    subtlePressed: a.secondary[200], // Subtle secondary pressed state
    mid: a.secondary[700],           // Mid-tone secondary (table headers)
  },
  /** Success interaction states (Green) */
  success: {
    base: a.success[800],            // Base success action color
    hover: a.success[900],           // Hover state for success actions
    pressed: a.success[950],         // Pressed state for success actions
  },
  /** Danger interaction states (Red) */
  danger: {
    base: a.danger[800],             // Base danger action color
    hover: a.danger[900],            // Hover state for danger actions
    pressed: a.danger[950],          // Pressed state for danger actions
  },
  /** Warning interaction states (Yellow) */
  warning: {
    base: a.warning[800],            // Base warning action color
    hover: a.warning[900],           // Hover state for warning actions
    pressed: a.warning[950],         // Pressed state for warning actions
  },
  /** Neutral interaction states */
  neutral: {
    base: p.neutral[600],            // Base neutral interactive color (checked)
    hover: p.neutral[700],           // Hover state for neutral actions (tooltip bg)
    subtle: p.neutral[400],          // Subtle neutral surface (unchecked border)
    subtleHover: p.neutral[500],     // Subtle neutral hover state
    subtleFocus: p.neutral[200],     // Subtle neutral focus state
  },
} as const;

// ════════════════════════════════════════════════════════════════════════════
// AGGREGATE EXPORT — Every semantic token in one object
// ════════════════════════════════════════════════════════════════════════════

export const SEMANTIC = {
  text,
  surface,
  border,
  states,
  focus,
  layout,
  radius,
  shadow,
  typography,
  transition,
  iconSizes,
  controlSize,
  interactive,
} as const;

// ════════════════════════════════════════════════════════════════════════════
// FACTORY — Dynamic semantic tokens with custom alias set
// ════════════════════════════════════════════════════════════════════════════

export interface AliasSet {
  primary: typeof a.primary;
  secondary: typeof a.secondary;
  surface: typeof a.surface;
  success: typeof a.success;
  warning: typeof a.warning;
  danger: typeof a.danger;
}

// ── Deep-merge utility for semantic overrides ──

type Widen<T> = T extends string ? string : T extends number ? number : T;
type DeepPartial<T> = { [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : Widen<T[P]> };

function deepMerge<T extends Record<string, unknown>>(target: T, source: DeepPartial<T>): T {
  const result = { ...target };
  for (const key of Object.keys(source) as (keyof T)[]) {
    const sv = source[key];
    const tv = target[key];
    if (sv !== undefined && tv !== undefined && typeof sv === 'object' && typeof tv === 'object' && !Array.isArray(sv)) {
      result[key] = deepMerge(tv as Record<string, unknown>, sv as DeepPartial<Record<string, unknown>>) as T[keyof T];
    } else if (sv !== undefined) {
      result[key] = sv as T[keyof T];
    }
  }
  return result;
}

export type SemanticOverrides = DeepPartial<SemanticTokens>;

function buildSemanticTokens(al: AliasSet) {
  return {
    text: {
      primary: al.primary[800],
      secondary: al.secondary[800],
      tertiary: al.secondary[700],
      muted: al.secondary[400],
      onColor: al.surface.white,
      disabled: p.neutral.white,
      success: al.success[800],
      danger: al.danger[800],
      warning: al.warning[800],
      link: al.primary[800],
      linkHover: al.primary[900],
      linkDisabled: p.neutral[200],
    },
    surface: {
      ground: al.surface.white,
      canvas: p.neutral[50],
      raised: p.neutral[50],
      modal: al.surface.white,
      disabled: p.neutral[200],
      dark: p.neutral[950],
    },
    border: {
      default: p.neutral[200],
      strong: p.neutral[600],
      divider: p.neutral[50],
      focus: al.primary[800],
      disabled: p.neutral[200],
      primary: al.primary[800],
      secondary: al.secondary[800],
      success: al.success[800],
      danger: al.danger[800],
      warning: al.warning[800],
      focusInput: al.secondary[400],
    },
    states: {
      success: { bg: al.success[50], fg: al.success[800], border: al.success[800] },
      warning: { bg: al.warning[50], fg: al.warning[800], border: al.warning[800] },
      danger: { bg: al.danger[50], fg: al.danger[800], border: al.danger[800] },
      info: { bg: p.neutral[50], fg: p.neutral[800], border: p.neutral[800] },
    },
    focus: {
      ring: al.primary[800],
      ringOffset: al.surface.white,
    },
    layout,
    radius,
    shadow,
    typography,
    transition,
    iconSizes,
    controlSize,
    interactive: {
      primary: {
        base: al.primary[800],
        hover: al.primary[900],
        pressed: al.primary[950],
        subtle: al.primary[50],
        subtlePressed: al.primary[100],
      },
      secondary: {
        base: al.secondary[800],
        hover: al.secondary[700],
        pressed: al.secondary[950],
        subtle: al.secondary[50],
        subtleHover: al.secondary[100],
        subtlePressed: al.secondary[200],
        mid: al.secondary[700],
      },
      success: {
        base: al.success[800],
        hover: al.success[900],
        pressed: al.success[950],
      },
      danger: {
        base: al.danger[800],
        hover: al.danger[900],
        pressed: al.danger[950],
      },
      warning: {
        base: al.warning[800],
        hover: al.warning[900],
        pressed: al.warning[950],
      },
      neutral: {
        base: p.neutral[600],
        hover: p.neutral[700],
        subtle: p.neutral[400],
        subtleHover: p.neutral[500],
        subtleFocus: p.neutral[200],
      },
    },
  };
}

export type SemanticTokens = ReturnType<typeof buildSemanticTokens>;

export function createSemanticTokens(al: AliasSet, overrides?: SemanticOverrides): SemanticTokens {
  const tokens = buildSemanticTokens(al);
  return overrides ? deepMerge(tokens, overrides) : tokens;
}

export const defaultAliases: AliasSet = {
  primary: a.primary,
  secondary: a.secondary,
  surface: a.surface,
  success: a.success,
  warning: a.warning,
  danger: a.danger,
};
